import React, { FC, HTMLAttributes, useRef, useState } from 'react';
import classNames from 'classnames';
import ReactQuill from 'react-quill';
import './quill.show.module.css';
import styles from './RichText.module.scss';
import { t, extractFromHtml, removeMultipleSpaces, removeImgFromString } from 'utils';

export interface IRichText extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  label: string;
  value: string;
  onChange: any;
  maxLength?: number;
  isLabelMandatory?: boolean;
  disabled?: boolean;
}

const RichText: FC<IRichText> = (props) => {
  let { className, label, value, onChange, maxLength = 400, isLabelMandatory, disabled } = props;
  if (isLabelMandatory) label = `${label} *`;
  const quillRef = useRef<any>(null);
  const [focused, setFocused] = useState(false);
  const [currentLength, setCurrentLength] = useState(0);

  const handleOnChange = (value: string, _: any, source: any): any => {
    // Only trigger onChange if user types in the box
    if (source === 'user') onChange(removeImgFromString(value));
  };

  const onFocus = (): any => {
    setFocused(true);
  };

  const onBlur = (): any => {
    setFocused(false);
    // remove highlight
    handleOnChange(removeHighlight(value), undefined, 'user');
  };

  const onKeyUp = (e: any): any => {
    if (typeof onChange !== 'undefined') {
      const editor = quillRef.current.getEditor();
      const unprivilegedEditor = quillRef.current.makeUnprivilegedEditor(editor);

      if (maxLength) {
        checkWordsCount(e);
      } else {
        onChange(unprivilegedEditor.getHTML());
      }
    }
  };

  const checkWordsCount = (event: any): any => {
    if (event && event.key === 'Meta') {
      const editor = quillRef.current.getEditor();
      const unprivilegedEditor = quillRef.current.makeUnprivilegedEditor(editor);
      const htmlContent = unprivilegedEditor.getHTML();

      // strip html tags to get real string length
      const strippedString = extractFromHtml(htmlContent);
      let error = false;

      if (maxLength) {
        if (strippedString.length >= +maxLength && event.key !== 'Backspace') {
          event.preventDefault();
          error = true;
        }
        // second param represents error
        onChange(htmlContent, error);
        setCurrentLength(strippedString.length);
      }
    }
  };

  const checkCharacterCount = (event: any): any => {
    const editor = quillRef.current.getEditor();
    const unprivilegedEditor = quillRef.current.makeUnprivilegedEditor(editor);

    // strip html tags to get real string length
    const strippedString = extractFromHtml(unprivilegedEditor.getHTML());
    const isKeyAllowed =
      event && ['Backspace', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key);
    if (maxLength) {
      if (strippedString.length >= maxLength && !isKeyAllowed) {
        event.preventDefault();
      }
      setCurrentLength(strippedString.length);
    }
  };

  const getPureValue = (): any => {
    const editor = quillRef.current?.getEditor();
    const unprivilegedEditor = quillRef.current?.makeUnprivilegedEditor(editor);
    // unprivilegedEditor is undefined if the editor is not focused
    return unprivilegedEditor
      ? extractFromHtml(unprivilegedEditor.getHTML())
      : extractFromHtml(removeMultipleSpaces(value)).trim();
  };

  const removeHighlight = (value: string): any => (value || '').replace(/color:\s([^;]+);/gi, '');

  // Prepare main class of the component
  const rootClassName = classNames(styles.root, className, {
    [styles.disabled]: !!disabled,
  });
  const editorClassName = classNames({
    // If focus is on the editor
    focus: focused,
    // If the editor has value
    filled: value,
    disabled: disabled,
  });

  const pureValue = getPureValue();
  let counter = 0;
  if (maxLength && pureValue) {
    counter = pureValue.length;
  } else if (!maxLength && pureValue) {
    counter = currentLength;
  }

  let limitLabel = '';
  if (maxLength) limitLabel = `${maxLength}`;
  let style = {};
  let bounceClass = '';
  if (maxLength && counter > maxLength) {
    style = { color: 'red' };
    bounceClass = 'bounce';
  }
  const limitLabelClasses = [styles.lengthIndicator, styles[bounceClass]];

  // Add toolbar icons
  const toolbarButtons = ['bold', 'italic', 'underline'];

  return (
    <div className={rootClassName}>
      <div className={styles.label}>{t(label)}</div>
      <ReactQuill
        className={editorClassName}
        ref={quillRef}
        modules={{ toolbar: toolbarButtons }}
        formats={['bold', 'italic', 'underline', 'link']}
        defaultValue={value}
        value={value}
        readOnly={disabled}
        onChange={handleOnChange}
        onKeyUp={onKeyUp}
        onKeyDown={checkCharacterCount}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <div className={limitLabelClasses.join(' ')} style={style}>
        {counter}/{limitLabel}
      </div>
    </div>
  );
};

export default RichText;
